import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Package, Store, Tag } from 'lucide-react';
import {
  formatPrice,
  getDisplayStock,
  getProductId,
  getProductImageUrl,
  getProductName,
  isProductOutOfStock,
} from '../../utils/helpers';

const ProductContextPanel = ({
  product,
  seller,
  activeOffer,
  variant = 'panel',
  className = '',
}) => {
  const { t } = useTranslation(['conversations', 'bids']);

  if (!product) {
    return (
      <div className={`mp-product-panel mp-product-panel--empty ${className}`}>
        <Store size={32} strokeWidth={1.25} />
        <p>{t('generalConversation', { defaultValue: 'General conversation' })}</p>
        <span className="mp-product-panel-hint">
          {t('attachProductHint', {
            defaultValue: 'Offers are tied to a product. Open a listing and tap Make offer.',
          })}
        </span>
      </div>
    );
  }

  const productId = getProductId(product);
  const name = getProductName(product);
  const price = product.price;
  const stock = getDisplayStock(product);
  const outOfStock = isProductOutOfStock(product);
  const storeName = seller?.name || seller?.storeName || product.brand;

  const offerStatus = activeOffer?.status;
  const offerAmount = activeOffer?.amount ?? activeOffer?.finalPrice;

  return (
    <div className={`mp-product-panel mp-product-panel--${variant} ${className}`}>
      <div className="mp-product-panel-media">
        <img src={getProductImageUrl(product)} alt={name} />
      </div>
      <div className="mp-product-panel-body">
        <p className="mp-product-panel-store">
          <Store size={14} />
          {storeName || t('officialStore')}
        </p>
        <h3 className="mp-product-panel-title">{name}</h3>
        <p className="mp-product-panel-price">{formatPrice(price)}</p>
        <p className={`mp-product-panel-stock ${outOfStock ? 'mp-product-panel-stock--out' : ''}`}>
          <Package size={14} />
          {outOfStock
            ? t('outOfStock', { defaultValue: 'Out of stock', ns: 'products' })
            : t('inStockCount', { defaultValue: '{{count}} available', count: stock })}
        </p>

        {activeOffer && offerStatus && (
          <div className={`mp-product-offer-status mp-product-offer-status--${offerStatus.toLowerCase()}`}>
            <Tag size={14} />
            <span>
              {t(`offerStatus.${offerStatus}`, {
                defaultValue: offerStatus,
                ns: 'conversations',
              })}
              {offerAmount != null && ` · ${formatPrice(offerAmount)}`}
            </span>
          </div>
        )}

        {productId && (
          <Link to={`/product/${productId}`} className="mp-product-panel-cta">
            <ExternalLink size={16} />
            {t('viewProduct', { defaultValue: 'View product' })}
          </Link>
        )}
      </div>
    </div>
  );
};

export default ProductContextPanel;
